import { Button, Theme } from '@mui/material';
import Appbar from 'components/appbar/Appbar';
import ModuleLoading from 'components/loading/ModuleLoading';
import TableLoading from 'components/loading/TableLoading';
import PageHeaderActions from 'components/page-header/PageHeaderActions';
import Pagination from 'components/pagination/Pagination';
import { useApp } from 'providers/app';
import PaginationProvider from 'providers/pagination';
import useSearch from 'hooks/useSearch';
import useTableOrder from 'hooks/useTableOrder';
import React, { useEffect, useState } from 'react';
import { api } from 'services/api';
import history from 'services/history';
import ResourcesActions from './PlansActions';
import ResourceModuleList from './list/module/PlanModuleList';
import PlanTableList from './list/table/PlanTableList';
import NoData from 'components/nodata/NoData';
import { plansTableTemplate } from './plansTableTemplate';
import { Plan } from 'types/plan';
import { moneyFormat } from 'helpers/NumberFormat';
import { makeStyles } from '@mui/styles';
import PlansFilterBox from './PlansFilterBox';
import TableContainer from 'components/table/TableContainer';

const useStyles = makeStyles<Theme>(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  filter: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 10,
    flex: 0.5,
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '0.5fr 1fr',
      flex: 1,
    },
  },
}));

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const app = useApp();
  const [displayMode, setDisplayMode] = useState<'list' | 'module'>('list');
  const search = useSearch();
  const [filtered, setFiltered] = useState<Plan[]>([]);
  const [orderedIndex, sort] = useTableOrder();
  const [searchValue, setSearchValue] = useState('');
  const [indexToSearch, setIndexToSearch] = useState('description');

  useEffect(() => {
    setDisplayMode(app.isMobile || app.windowWidth < 930 ? 'module' : 'list');
  }, [app.isMobile, app.windowWidth]);

  useEffect(() => {
    setFiltered(plans);
  }, [plans]);

  useEffect(() => {
    setLoading(true);
    api
      .get<Plan[]>('/plans')
      .then(response => {
        setPlans(
          response.data.map(plan => {
            plan.formattedPrice = moneyFormat(plan.price);
            plan.formattedIsVisible = plan.is_visible ? 'Sim' : 'Não';
            return plan;
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const p = search(searchValue, indexToSearch, plans);
    setFiltered(p);
  }, [indexToSearch, searchValue, search, plans]);

  function handleSort(index: string) {
    const f = sort(index, filtered);
    setFiltered(f);
  }

  return (
    <>
      <Appbar title="Planos" ActionsComponent={<ResourcesActions />} />
      <PageHeaderActions
        title="Planos"
        description="Gestão dos planos"
        ActionComponent={
          <Button color="primary" variant="contained" size="small" onClick={() => history.push('/plan')}>
            Adicionar
          </Button>
        }
      />
      <TableContainer tableTemplate={plansTableTemplate}>
        <PlansFilterBox
          displayMode={displayMode}
          indexToSearch={indexToSearch}
          searchValue={searchValue}
          setDisplayMode={setDisplayMode}
          setIndexToSearch={setIndexToSearch}
          setSearchValue={setSearchValue}
        />
        {loading ? (
          displayMode === 'list' ? (
            <TableLoading />
          ) : (
            <ModuleLoading />
          )
        ) : filtered.length === 0 ? (
          <NoData message="Nenhum recurso para exibir" />
        ) : (
          <PaginationProvider>
            <div className={classes.container}>
              {displayMode === 'list' ? (
                <PlanTableList plans={filtered} handleSort={handleSort} orderedIndex={orderedIndex} />
              ) : (
                <ResourceModuleList plans={filtered} />
              )}
              <Pagination count={filtered.length} />
            </div>
          </PaginationProvider>
        )}
      </TableContainer>
    </>
  );
};

export default Plans;
